import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Token storage with session fallback for enhanced security
// Uses sessionStorage when available (clears on tab close) for sensitive sessions
const TOKEN_KEY = "farmwatch_token";
const USE_SESSION_STORAGE = false; // Set true for session-only tokens

// Secure token storage helpers
const tokenStorage = {
  get: () => {
    try {
      const storage = USE_SESSION_STORAGE ? sessionStorage : localStorage;
      const token = storage.getItem(TOKEN_KEY);
      // Basic validation - JWT should have 3 parts separated by dots
      if (token && token.split('.').length === 3) {
        return token;
      }
      return null;
    } catch {
      return null;
    }
  },
  set: (token) => {
    try {
      if (!token || token.split('.').length !== 3) return false;
      const storage = USE_SESSION_STORAGE ? sessionStorage : localStorage;
      storage.setItem(TOKEN_KEY, token);
      return true;
    } catch {
      return false;
    }
  },
  remove: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      // Storage unavailable
    }
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => tokenStorage.get());
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      tokenStorage.remove();
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    tokenStorage.set(newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (email, name, password) => {
    const response = await axios.post(`${API}/auth/register`, { email, name, password });
    const { token: newToken, user: userData } = response.data;
    tokenStorage.set(newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    tokenStorage.remove();
    setToken(null);
    setUser(null);
  }, []);

  const saveArticle = useCallback(async (articleId) => {
    const response = await axios.post(
      `${API}/user/saved-articles`,
      { article_id: articleId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setUser(prev => ({ ...prev, saved_articles: response.data.saved_articles }));
    return response.data;
  }, [token]);

  const unsaveArticle = useCallback(async (articleId) => {
    const response = await axios.delete(
      `${API}/user/saved-articles/${articleId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setUser(prev => ({ ...prev, saved_articles: response.data.saved_articles }));
    return response.data;
  }, [token]);

  const createPriceAlert = useCallback(async (alert) => {
    const response = await axios.post(
      `${API}/user/price-alerts`,
      alert,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setUser(prev => ({
      ...prev,
      price_alerts: [...(prev.price_alerts || []), response.data.alert]
    }));
    return response.data;
  }, [token]);

  const deletePriceAlert = useCallback(async (alertId) => {
    await axios.delete(
      `${API}/user/price-alerts/${alertId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setUser(prev => ({
      ...prev,
      price_alerts: (prev.price_alerts || []).filter(a => a.id !== alertId)
    }));
  }, [token]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    saveArticle,
    unsaveArticle,
    createPriceAlert,
    deletePriceAlert,
    isAdmin: user?.role === "admin"
  }), [user, token, loading, login, register, logout, saveArticle, unsaveArticle, createPriceAlert, deletePriceAlert]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
